/* eslint-disable no-await-in-loop */
import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Paragraph } from '@fs/zion-ui'
import React, { useCallback, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { deleteIdFromCache } from '../../../services/membershipService'
import { UserContext } from '../UserContext'
import { getTreeSyncStatus, treeSync } from '../../../services/treeSproutService'

export default function CreatePedigreeDialog({ ...opener }) {
  const { t } = useTranslation()
  const { currentUser, fetchCurrentUser } = useContext(UserContext)
  const { error } = useNotifications()
  const getStatusCount = useRef(0)
  const GET_STATUS_MAX_COUNT = 5

  const getSyncStatus = useCallback(async () => {
    let treeSyncCompleted = false
    while (getStatusCount.current < GET_STATUS_MAX_COUNT && !treeSyncCompleted) {
      getStatusCount.current += 1
      await new Promise((resolve) => {
        setTimeout(resolve, 500 * getStatusCount.current)
      })
      const treeSyncStatus = await getTreeSyncStatus(currentUser.dmcUser.cisId)
      if (treeSyncStatus.status === 'COMPLETED') {
        treeSyncCompleted = true
        opener.close()
      }
    }

    if (!treeSyncCompleted) {
      error(
        t('pedigree.error.createPedigreeNotCompleted', 'Pedigree creation not yet completed. Please check again later.')
      )
    }
  }, [currentUser.dmcUser.cisId, error, opener, t])

  const confirmBtnOnClick = async () => {
    try {
      opener.close()
      await deleteIdFromCache(currentUser.dmcUser.cisId)
      await treeSync(currentUser.dmcUser.cisId)
      await getSyncStatus()
      fetchCurrentUser()
    } catch (err) {
      error(
        t('user.errors.creatingPedigree', {
          defaultValue: `Error creating pedigree: ${err.message || ''}`,
          message: err.message || '',
        }),
        err
      )
    }
  }

  return (
    <DialogOverlay
      {...opener}
      title={t('user.buttons.createPedigree.description', 'Create Pedigree')}
      subtitle={currentUser.dmcUser?.cisId?.split('.')[2]}
    >
      <Paragraph size="sm">
        {t(
          'user.buttons.createPedigree.dialog.body',
          'By running this feature, we will attempt to add family members to Family Tree based on the membership information for this user. The information will show contributed by the user and may be duplicates to data already in Family Tree. Do you want to continue?'
        )}
      </Paragraph>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={confirmBtnOnClick}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={opener.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
